// =============================================================================
// VandieZ admin-web -- Pending promotion awards (/promotions/awards/pending)
// =============================================================================
// Cross-program triage view of every promotion award that has been
// computed but not yet settled. Ops uses this page to chase down
// outliers (referrals that never converted, monthly winners that need a
// manual override) and to claw back awards that turned out to be
// fraudulent or paid by mistake.
//
//   Days pending  — age of the award (now - awarded_at) so the most
//                   stale rows float to the top of the operator's
//                   attention.
//   Program       — program_code shortcut (driver_referral,
//                   driver_of_month, ...).
//   User          — user_full_name fallback to user_id when the join
//                   failed (e.g. user was hard-deleted).
//   Amount        — award amount as configured per program.
//   [Clawback]    — POST /admin/promotions/awards/:id/clawback with a
//                   short reason (≥4 chars enforced backend-side).
//                   On success we refetch so the row drops away.
//
// Backed by promotionsAdminApi.pendingAwards + promotionsAdminApi.clawback.
// Same Card/PageHeader/ApiError pattern as PromotionsIndexPage.
// (Spec 7, Task 32.)
// =============================================================================

import { AlertCircle, Gift, Undo2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { promotionsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ApiError } from "@/types/auth";
import type { PromotionAwardRead } from "@/types/promotion";

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return 0;
  }
  const diffMs = Date.now() - then;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function PromotionsPendingPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [awards, setAwards] = useState<PromotionAwardRead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [clawingBackId, setClawingBackId] = useState<string | null>(null);

  const fetchPending = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await promotionsAdminApi.pendingAwards(
          accessToken,
          { limit: 100, offset: 0 },
          signal,
        );
        setAwards(res.awards);
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
          toast.error(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // unmount — silent
        } else {
          const msg = "Unexpected error loading pending awards.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, toast],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPending(controller.signal);
    return () => { controller.abort(); };
  }, [fetchPending]);

  const handleClawback = useCallback(
    async (award: PromotionAwardRead): Promise<void> => {
      if (accessToken === null) {return;}
      const reason = window.prompt(
        `Clawback reason for ${award.user_full_name ?? award.user_id} (≥4 chars)?`,
      );
      if (reason === null) {return;}
      if (reason.trim().length < 4) {
        toast.error("Reason must be at least 4 characters.");
        return;
      }
      setClawingBackId(award.id);
      try {
        await promotionsAdminApi.clawback(accessToken, award.id, reason.trim());
        toast.success(`Clawed back ${award.amount}.`);
        await fetchPending();
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          toast.error(e.problem.detail);
        } else {
          toast.error("Unexpected error during clawback.");
        }
      } finally {
        setClawingBackId(null);
      }
    },
    [accessToken, fetchPending, toast],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Pending promotion awards"
        description="Cross-program list of awards that have been computed but not yet settled. Use Clawback to reverse a fraudulent or mistaken award."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-warning-subtle-foreground" />
            Pending
          </CardTitle>
          <CardDescription>
            {loading
              ? "Loading…"
              : `${awards.length} pending award${awards.length === 1 ? "" : "s"}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && awards.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              Loading...
            </div>
          ) : awards.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No pending awards. All caught up.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4 text-right">Days pending</th>
                    <th className="py-2 pr-4">Program</th>
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody>
                  {awards.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b last:border-b-0 hover:bg-muted/40"
                    >
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {daysSince(a.awarded_at)}
                      </td>
                      <td className="py-3 pr-4 text-sm">
                        {a.program_code ?? a.program_id}
                      </td>
                      <td className="py-3 pr-4 font-medium">
                        {a.user_full_name ?? a.user_id}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {a.amount}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={clawingBackId === a.id}
                          onClick={() => { void handleClawback(a); }}
                        >
                          <Undo2 className="mr-1 h-4 w-4" />
                          {clawingBackId === a.id ? "Clawing…" : "Clawback"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PromotionsPendingPage;
